import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';

// Use the existing Markdown parser for legacy AI notes, with three restricted
// inline additions. Never parse raw HTML or allow embedded images/scripts.
function formatNoteMarks() {
    const styles = { '++': ['u', {}], '==': ['mark', {}], '^^': ['mark', { 'data-pink': 'true' }] };
    function pair(parts) {
        const out = [];
        for (let i = 0; i < parts.length; i++) {
            const node = parts[i];
            if (node.type !== 'noteMarker') { out.push(node); continue; }
            let end = i + 1;
            while (end < parts.length && !(parts[end].type === 'noteMarker' && parts[end].value === node.value)) end++;
            if (end < parts.length && end > i + 1) {
                const [hName, hProperties] = styles[node.value];
                out.push({ type: 'noteStyle', data: { hName, hProperties }, children: pair(parts.slice(i + 1, end)) }); i = end;
            } else out.push({ type: 'text', value: node.value });
        }
        return out;
    }
    const visit = node => {
        if (!node.children) return;
        node.children.forEach(visit);
        // Each paragraph/inline container is independent; marks cannot swallow lists.
        if (node.children.some(n => n.type === 'text')) node.children = pair(node.children.flatMap(n => n.type === 'text' ? n.value.split(/(\+\+|==|\^\^)/).filter(Boolean).map(value => ({ type: styles[value] ? 'noteMarker' : 'text', value })) : [n]));
    };
    return visit;
}
export function noteHtml(body) {
    return renderToStaticMarkup(React.createElement(ReactMarkdown, {
        remarkPlugins: [remarkGfm, remarkBreaks, formatNoteMarks],
        allowedElements: ['p', 'br', 'strong', 'em', 'del', 'u', 'mark', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'code', 'pre'],
        unwrapDisallowed: true,
    }, String(body || ''))).replace(/>\n</g, '><').replace(/<br\/>\n/g, '<br/>');
}
export function noteText(root) {
    const walk = node => {
        if (node.nodeType === 3) return node.textContent;
        if (node.nodeType !== 1) return '';
        const tag = node.tagName.toLowerCase();
        if (['script', 'style', 'iframe', 'img'].includes(tag)) return '';
        if (tag === 'br') return '\n';
        let value = [...node.childNodes].map(walk).join('');
        const style = node.style || {};
        const bg = style.backgroundColor || '';
        if (tag === 'mark' || bg) value = (node.dataset?.pink || /255, 182, 213|ffb6d5/i.test(bg) ? '^^' : '==') + value + (node.dataset?.pink || /255, 182, 213|ffb6d5/i.test(bg) ? '^^' : '==');
        if (['b', 'strong'].includes(tag) || style.fontWeight === 'bold') value = '**' + value + '**';
        if (['i', 'em'].includes(tag) || style.fontStyle === 'italic') value = '*' + value + '*';
        if (tag === 'u' || style.textDecoration?.includes('underline')) value = '++' + value + '++';
        if (['s', 'strike', 'del'].includes(tag) || style.textDecoration?.includes('line-through')) value = '~~' + value + '~~';
        if (/^h[1-6]$/.test(tag)) value = '**' + value + '**';
        if (tag === 'li') value = '\n• ' + value;
        else if ((['div', 'p', 'blockquote', 'pre'].includes(tag) || /^h[1-6]$/.test(tag)) && node !== root) value = '\n' + value;
        return value;
    };
    return [...root.childNodes].map(walk).join('').replace(/^\n/, '');
}
export const noteStyles = '.note-rich{white-space:pre-wrap;overflow-wrap:anywhere;line-height:1.7}.note-rich mark{background:#fff18a!important;color:#222!important;-webkit-print-color-adjust:exact;print-color-adjust:exact}.note-rich mark[data-pink]{background:#ffb6d5!important}.note-rich strong{font-weight:700}.note-rich em{font-style:italic}.note-rich u{text-decoration:underline}.note-rich s,.note-rich del{text-decoration:line-through}.note-rich p{margin:0 0 .65em}.note-rich ul{list-style:disc;padding-left:1.5em;margin:.5em 0}.note-rich ol{list-style:decimal;padding-left:1.5em;margin:.5em 0}.note-rich h1,.note-rich h2,.note-rich h3,.note-rich h4{font-size:1.1em;font-weight:700;margin:.7em 0}.note-rich pre{white-space:pre-wrap}';
